import { shortSpinId } from '../../../Model/Spin/spinId.js'
import { Fragment, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

const BOLD_BIN_URL = 'http://www.boldsystems.org/index.php/Public_BarcodeCluster?clusteruri='

/** A list on one line, cut at the cell's width, with the whole list as its tooltip; rows keep one height. */
export function ListCell({ items, children }: { items: string[]; children?: ReactNode }) {
  const text = items.join(', ')
  return (
    <span data-testid="otu-list-cell" className="block max-w-[16rem] truncate" title={text}>
      {children ?? text}
    </span>
  )
}

const strings = (value: unknown): (string | null)[] => (Array.isArray(value) ? (value as (string | null)[]) : [])

const distinct = (items: string[]): string[] => [...new Set(items)]

export const processIdCell = (value: unknown) => <ListCell items={strings(value).filter((x): x is string => !!x)} />

/** Y or N per distinct value; an OTU without a BOLD result counts as N, as in the export. */
export const binSharingCell = (value: unknown) => (
  <ListCell items={distinct((Array.isArray(value) ? (value as (boolean | null)[]) : []).map((x) => (x === true ? 'Y' : 'N')))} />
)

export const binsCell = (value: unknown) => {
  const bins = strings(value)
    .filter((x): x is string => !!x)
    .map((x) => x.replace(BOLD_BIN_URL, ''))
  return (
    <ListCell items={bins}>
      {bins.map((bin, i) => (
        <Fragment key={bin}>
          {i > 0 && ', '}
          <a className="link" target="_blank" rel="noopener noreferrer" href={`${BOLD_BIN_URL}${bin}`}>
            {bin}
          </a>
        </Fragment>
      ))}
    </ListCell>
  )
}

export const binSpeciesCell = (value: unknown) => (
  <ListCell items={distinct(strings(value).flatMap((x) => (x ? x.split('|') : [])).filter((x) => x.trim() !== ''))} />
)

/** A SPIN's id, abbreviated git-hash style, linking to its shareable page. */
export const spinIdCell = (value: unknown) => {
  const id = typeof value === 'string' ? value : ''
  return (
    <Link to={`/spins/${id}`} className="link link-primary font-mono text-xs" title={id}>
      {id ? shortSpinId(id) : '—'}
    </Link>
  )
}
